import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link as RouterLink, Navigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./authConfig";
import { AppAnalyticsTracker } from './components/AppAnalyticsTracker';

// MUI and Theme Imports
import { ThemeProvider } from '@mui/material/styles';
import { darkTheme, cyber } from './theme';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Container, 
  Typography, 
  CssBaseline, 
  Paper, 
  Alert, 
  AlertTitle, 
  Link, 
  FormControlLabel, 
  Checkbox,
  styled,
  alpha,
  Fade,
  Stack,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SecurityIcon from '@mui/icons-material/Security';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SpeedIcon from '@mui/icons-material/Speed';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LoginIcon from '@mui/icons-material/Login';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Component Imports
import Layout from './components/Layout/Layout';
import PublicLayout from './components/Layout/PublicLayout';
import Dashboard from './pages/Dashboard';
import ReportDetail from './pages/ReportDetail';
import About from './pages/About';
import Feedback from './pages/Feedback';
import SubmitArticle from './pages/SubmitArticle';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import LandingPage from './pages/LandingPage';
import ProfilePage from './pages/ProfilePage';
import StatisticsPage from './pages/StatisticsPage';
import IOCSearchPage from './pages/IOCSearchPage';
import CVESearchPage from './pages/CVESearchPage';  // NEW: CVE Search Page
import KnowledgeGraph from './pages/KnowledgeGraph';
import EntitiesIndexPage from './pages/EntitiesIndexPage';
import EntityProfilePage from './pages/EntityProfilePage';
import KnowledgeGraphAbout from './pages/KnowledgeGraph/About';
import Roadmap from './pages/Roadmap';
import AccessCodePage from './pages/AccessCodePage';
import WeeklyBriefingPage from './pages/WeeklyBriefingPage';
import Research from './pages/Research';
import McpIntegrationPage from './pages/McpIntegrationPage';
import STIXBundlesPage from './pages/STIXBundlesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AnalyticsReportPage from './pages/AnalyticsReportPage';
import PublicReportsPage from './pages/PublicReportsPage';
import PublicStatisticsPage from './pages/PublicStatisticsPage';

// --- Styled Components for Login Page ---
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${cyber.bgDefault} 0%, ${cyber.bgSidebar} 50%, ${cyber.bgPaper} 100%)`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(123, 76, 255, 0.12) 0%, transparent 70%)',
    animation: 'pulse 15s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.1)' },
  }
}));

const LoginCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxWidth: '550px',
  position: 'relative',
  zIndex: 1,
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(20px)',
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: cyber.glow,
    transform: 'translateY(-3px)',
  }
}));

const LogoBox = styled(Box)(({ theme }) => ({
  width: 100,
  height: 100,
  borderRadius: '50%',
  background: cyber.gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(123, 76, 255, 0.4)',
  animation: 'float 3s ease-in-out infinite',
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-10px)' },
  }
}));

const FeatureBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  background: alpha(theme.palette.primary.main, 0.05),
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.1),
    transform: 'translateX(4px)',
  }
}));

const SignInButton = styled(Button)(({ theme }) => ({
  background: cyber.gradient,
  color: 'white',
  fontWeight: 'bold',
  padding: '14px 32px',
  fontSize: '1rem',
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(123, 76, 255, 0.4)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    background: cyber.gradientHover,
    boxShadow: '0 6px 24px rgba(123, 76, 255, 0.6)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  }
}));

// --- Cookie Consent Banner ---
const CookieConsentBanner = ({ onAccept }: { onAccept: () => void }) => {
    return (
        <Paper elevation={6} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, p: 2, bgcolor: 'background.paper', zIndex: 2000 }}>
            <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                    This site uses essential cookies for authentication and may use cookies for analytics. By clicking "Accept", you agree to our use of cookies.
                </Typography>
                <Button variant="contained" onClick={onAccept}>Accept</Button>
            </Container>
        </Paper>
    );
};

// --- Styled Components for Attention Banner ---
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.spacing(2),
        background: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        padding: theme.spacing(2),
        maxWidth: '500px',
    }
}));

const BetaBadge = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    background: cyber.gradient,
    borderRadius: theme.spacing(1),
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
}));

const FeatureItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    background: alpha(theme.palette.primary.main, 0.05),
    borderRadius: theme.spacing(1),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    marginBottom: theme.spacing(1),
}));

// --- Loading Indicator ---
const LoadingIndicator = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress />
    </Box>
);

// --- Modern Sign In Page ---
const SignInPage = () => {
    const { instance } = useMsal();
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const handleLogin = () => {
        if (!agreedToTerms) {
            alert("You must agree to the Terms of Service and Privacy Policy to continue.");
            return;
        }
        
        // Salva l'URL corrente per il redirect dopo login (solo per pagine protette)
        const currentPath = window.location.pathname;
        if (currentPath && currentPath !== '/' && !currentPath.startsWith('/terms') && !currentPath.startsWith('/privacy') && !currentPath.startsWith('/landingpage') && !currentPath.startsWith('/analytics')) {
            sessionStorage.setItem('postLoginRedirect', currentPath);
        }
        
        instance.loginPopup(loginRequest)
            .then(response => {
                instance.setActiveAccount(response.account);
            })
            .catch(e => console.error(e));
    };

    return (
        <LoginContainer>
            <Container maxWidth="sm">
                <Fade in timeout={600}>
                    <LoginCard elevation={8}>
                        {/* Logo */}
                        <LogoBox>
                            <Box
                                component="img"
                                sx={{ height: 60, width: 60 }}
                                alt="TI Mindmap HUB Logo"
                                src="/TI-Mindmap-HUB-logo.png"
                            />
                        </LogoBox>

                        {/* Title */}
                        <Typography 
                            component="h1" 
                            variant="h4" 
                            fontWeight="bold"
                            sx={{ 
                                mb: 1,
                                ...cyber.gradientText,
                            }}
                        >
                            TI Mindmap HUB
                        </Typography>
                        
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                mb: 1,
                                px: 2,
                                py: 0.5,
                                bgcolor: alpha(cyber.primary, 0.1),
                                borderRadius: 2,
                                fontWeight: 'bold',
                                color: 'primary.main'
                            }}
                        >
                            BETA
                        </Typography>

                        <Typography color="text.secondary" sx={{ mb: 4 }}>
                            AI-Powered Threat Intelligence Platform
                        </Typography>

                        {/* Features Grid */}
                        <Stack spacing={1.5} sx={{ width: '100%', mb: 3 }}>
                            <FeatureBox>
                                <PsychologyIcon sx={{ color: cyber.accent.coral }} />
                                <Typography variant="body2" fontWeight="500">
                                    AI-Generated Analysis & Summaries
                                </Typography>
                            </FeatureBox>
                            <FeatureBox>
                                <SpeedIcon sx={{ color: cyber.accent.cyan }} />
                                <Typography variant="body2" fontWeight="500">
                                    Automated IOC & STIX Extraction
                                </Typography>
                            </FeatureBox>
                            <FeatureBox>
                                <SecurityIcon sx={{ color: cyber.accent.green }} />
                                <Typography variant="body2" fontWeight="500">
                                    Agentic Threat Intelligence
                                </Typography>
                            </FeatureBox>
                        </Stack>

                        {/* AI Warning Alert */}
                        <Alert 
                            severity="warning" 
                            icon={<WarningAmberIcon />} 
                            sx={{ 
                                width: '100%',
                                mb: 3,
                                borderRadius: 2,
                                '& .MuiAlert-icon': {
                                    color: 'warning.main'
                                }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 'bold' }}>Generative AI Content</AlertTitle>
                            <Typography variant="body2">
                                All summaries and analyses are AI-generated. Please verify critical information against original sources.
                            </Typography>
                        </Alert>

                        {/* Terms Checkbox */}
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={agreedToTerms} 
                                    onChange={(e) => setAgreedToTerms(e.target.checked)} 
                                    name="agreedToTerms"
                                    sx={{
                                        color: 'primary.main',
                                        '&.Mui-checked': {
                                            color: 'primary.main',
                                        }
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" color="text.secondary">
                                    I agree to the{' '}
                                    <Link 
                                        component={RouterLink} 
                                        to="/terms" 
                                        target="_blank"
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        Terms of Service
                                    </Link>
                                    {' '}and{' '}
                                    <Link 
                                        component={RouterLink} 
                                        to="/privacy" 
                                        target="_blank"
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        Privacy Policy
                                    </Link>
                                </Typography>
                            }
                            sx={{ mb: 3, alignItems: 'flex-start' }}
                        />

                        {/* Sign In Button */}
                        <SignInButton 
                            fullWidth
                            onClick={handleLogin} 
                            disabled={!agreedToTerms}
                            startIcon={<LoginIcon />}
                        >
                            Sign In
                        </SignInButton>

                        {/* Guest access banner */}
                        <Box sx={{ width: '100%', mt: 2 }}>
                            <Alert
                                severity="info"
                                icon={<AutoAwesomeIcon fontSize="inherit" />}
                                sx={{ borderRadius: 2, alignItems: 'center' }}
                                action={
                                    <Button
                                        component={RouterLink}
                                        to="/reports"
                                        size="small"
                                        variant="outlined"
                                        color="inherit"
                                        sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                                    >
                                        Continue as guest
                                    </Button>
                                }
                            >
                                <Typography variant="body2" fontWeight={600}>No account needed</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Browse reports, statistics and analytics without signing in.
                                </Typography>
                            </Alert>
                        </Box>

                        {/* Disclaimer */}
                        <Box 
                            sx={{ 
                                mt: 4, 
                                pt: 3, 
                                borderTop: 1, 
                                borderColor: 'divider',
                                width: '100%'
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                                <VerifiedUserIcon sx={{ fontSize: 20, color: cyber.accent.green }} />
                                <Typography variant="caption" color="text.secondary" fontWeight="600">
                                    Secure Authentication
                                </Typography>
                            </Stack>
                            <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                    display: 'block', 
                                    textAlign: 'center',
                                    lineHeight: 1.5
                                }}
                            >
                                The information provided is for informational purposes only. 
                                The creators assume no liability for any errors or omissions.
                            </Typography>
                        </Box>
                    </LoginCard>
                </Fade>
            </Container>
        </LoginContainer>
    );
};

// --- Main App Component ---
function App() {
    const { instance, inProgress, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [showCookieBanner, setShowCookieBanner] = useState(() => !localStorage.getItem('cookieConsent'));
    const [authStatus, setAuthStatus] = useState<'checking' | 'authorized' | 'unauthorized'>('checking');
    const [ssoAttempted, setSsoAttempted] = useState(false);
    const [isBetaModalOpen, setIsBetaModalOpen] = useState(false);

    // --- SSO LOGIC WITH LOOP PREVENTION ---
    useEffect(() => {
        if (!ssoAttempted && !isAuthenticated && inProgress === InteractionStatus.None) {
            setSsoAttempted(true);
            instance.ssoSilent(loginRequest).catch(error => {
                console.info("Silent SSO failed, manual login required.", error);
            });
        }
    }, [instance, isAuthenticated, inProgress, ssoAttempted]);

    // --- Authorization check after login (MODIFIED TO BYPASS ACCESS CODE) ---
    useEffect(() => {
        if (!isAuthenticated) {
            setAuthStatus('checking');
            return;
        }

        // Se l'utente è autenticato (isAuthenticated) e le operazioni MSAL
        // sono concluse (InteractionStatus.None), impostalo subito come autorizzato.
        if (inProgress === InteractionStatus.None && accounts.length > 0) {
            setAuthStatus('authorized');
            
            // --- LOGICA REDIRECT DOPO LOGIN ---
            // 1. Prima controlla sessionStorage (per login dalla pagina report SEO)
            const savedRedirect = sessionStorage.getItem('postLoginRedirect');
            if (savedRedirect) {
                sessionStorage.removeItem('postLoginRedirect');
                window.location.href = savedRedirect;
                return; // Evita di aprire il popup beta durante il redirect
            }
            
            // 2. Poi controlla query param ?redirect= (per link esterni)
            const urlParams = new URLSearchParams(window.location.search);
            const redirectPath = urlParams.get('redirect');
            if (redirectPath && redirectPath.startsWith('/')) {
                window.history.replaceState({}, '', window.location.pathname);
                window.location.href = redirectPath;
                return; // Evita di aprire il popup beta durante il redirect
            }
            // --- FINE LOGICA REDIRECT ---
            
            // --- LOGICA POPUP BETA ---
            // Controlla se l'utente ha già visto l'avviso in questa sessione
            const hasSeenWarning = sessionStorage.getItem('hasSeenBetaWarning');
            if (!hasSeenWarning) {
                // Se non l'ha visto, apri il popup
                setIsBetaModalOpen(true);
            }
            // --- FINE LOGICA POPUP BETA ---
        }

    }, [isAuthenticated, inProgress, accounts]);

    const handleCookieAccept = () => {
        localStorage.setItem('cookieConsent', 'true');
        setShowCookieBanner(false);
    };

    const handleCloseBetaModal = () => {
        setIsBetaModalOpen(false);
        // Impostiamo un flag per non mostrare più il popup in questa sessione
        sessionStorage.setItem('hasSeenBetaWarning', 'true');
    };

    const PageToRender = () => {
        if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect || (inProgress === InteractionStatus.SsoSilent && !ssoAttempted)) {
            return <LoadingIndicator />;
        }
    
        if (!isAuthenticated) {
            return (
                <BrowserRouter>
                    <AppAnalyticsTracker />
                    <Routes>
                        <Route path="*" element={<SignInPage />} />
                        <Route path="/terms" element={<PublicLayout><TermsOfService /></PublicLayout>} />
                        <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
                        <Route path="/landingpage" element={<PublicLayout><LandingPage /></PublicLayout>} />
                        <Route path="/research" element={<PublicLayout><Research /></PublicLayout>} />
                        <Route path="/reports" element={<PublicLayout><PublicReportsPage /></PublicLayout>} />
                        <Route path="/stats" element={<PublicLayout><PublicStatisticsPage /></PublicLayout>} />
                        <Route path="/analytics" element={<PublicLayout><AnalyticsPage /></PublicLayout>} />
                        <Route path="/analytics/:slug" element={<PublicLayout><AnalyticsReportPage /></PublicLayout>} />
                    </Routes>
                </BrowserRouter>
            );
        }

        if (authStatus === 'checking') {
            return <LoadingIndicator />;
        }

        if (authStatus === 'unauthorized') {
            return <AccessCodePage onSuccess={() => setAuthStatus('authorized')} />;
        }
    
        return (
            <BrowserRouter>
                <AppAnalyticsTracker />
                <Layout>
                    <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/report/:reportId" element={<ReportDetail />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/submit" element={<SubmitArticle />} />
                        <Route path="/knowledge-graph" element={<KnowledgeGraph />} />
                        <Route path="/entities" element={<EntitiesIndexPage />} />
                        <Route path="/entity/:canonId" element={<EntityProfilePage />} />
                        <Route path="/knowledge-graph/about" element={<KnowledgeGraphAbout />} />
                        <Route path="/roadmap" element={<Roadmap />} />
                        <Route path="/research" element={<Research />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/feedback" element={<Feedback />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/statistics" element={<StatisticsPage />} />
                        <Route path="/ioc-search" element={<IOCSearchPage />} />
                        <Route path="/cve-search" element={<CVESearchPage />} />  {/* NEW: CVE Search Route */}
                        <Route path="/stix-bundles" element={<STIXBundlesPage />} />  {/* NEW: STIX Bundles Route */}
                        <Route path="/briefing" element={<WeeklyBriefingPage />} />
                        <Route path="/mcp-integration" element={<McpIntegrationPage />} />
                        <Route path="/reports" element={<Navigate to="/" replace />} />
                        <Route path="/stats" element={<Navigate to="/statistics" replace />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="/analytics/:slug" element={<AnalyticsReportPage />} />
                    </Routes>
                </Layout>
            </BrowserRouter>
        );
    }

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            {showCookieBanner && <CookieConsentBanner onAccept={handleCookieAccept} />}
            
            {/* === INIZIO POPUP BETA === */}
            <StyledDialog
                open={isBetaModalOpen}
                onClose={handleCloseBetaModal}
                aria-labelledby="beta-warning-title"
                aria-describedby="beta-warning-description"
            >
                <DialogContent sx={{ p: 3 }}>
                    {/* Icon & Badge */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: cyber.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 2,
                                boxShadow: '0 8px 24px rgba(123, 76, 255, 0.4)',
                            }}
                        >
                            <PsychologyIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                        <BetaBadge>
                            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                            BETA VERSION
                        </BetaBadge>
                    </Box>

                    {/* Title */}
                    <Typography 
                        variant="h5" 
                        fontWeight="bold" 
                        gutterBottom
                        sx={{ textAlign: 'center', mb: 2 }}
                    >
                        Welcome to TI Mindmap HUB Beta
                    </Typography>

                    {/* Description */}
                    <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 3 }}>
                        You're accessing an experimental version of our AI-powered threat intelligence platform. 
                        Here's what you should know:
                    </Typography>

                    {/* Features/Warnings List */}
                    <Stack spacing={1} sx={{ mb: 3 }}>
                        <FeatureItem>
                            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 24, flexShrink: 0 }} />
                            <Box>
                                <Typography variant="body2" fontWeight="600">
                                    Experimental Features
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Some functionalities are under active development and testing
                                </Typography>
                            </Box>
                        </FeatureItem>

                        <FeatureItem>
                            <PsychologyIcon sx={{ color: 'primary.main', fontSize: 24, flexShrink: 0 }} />
                            <Box>
                                <Typography variant="body2" fontWeight="600">
                                    AI-Generated Content
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Always verify critical information against original sources
                                </Typography>
                            </Box>
                        </FeatureItem>

                        <FeatureItem>
                            <SpeedIcon sx={{ color: 'info.main', fontSize: 24, flexShrink: 0 }} />
                            <Box>
                                <Typography variant="body2" fontWeight="600">
                                    Continuous Improvements
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    We're constantly updating and enhancing the platform
                                </Typography>
                            </Box>
                        </FeatureItem>
                    </Stack>

                    {/* Disclaimer */}
                    <Alert 
                        severity="info" 
                        sx={{ 
                            borderRadius: 2,
                            border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                        }}
                    >
                        <Typography variant="caption">
                            <strong>Note:</strong> This is a research-driven project. Your feedback helps us improve. 
                            Please report any issues through the Feedback page.
                        </Typography>
                    </Alert>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={handleCloseBetaModal} 
                        fullWidth
                        variant="contained"
                        sx={{
                            background: cyber.gradient,
                            color: 'white',
                            fontWeight: 'bold',
                            padding: '12px 24px',
                            borderRadius: 1,
                            textTransform: 'none',
                            boxShadow: '0 4px 16px rgba(123, 76, 255, 0.4)',
                            '&:hover': {
                                background: cyber.gradientHover,
                                boxShadow: '0 6px 24px rgba(123, 76, 255, 0.6)',
                            }
                        }}
                    >
                        I Understand, Continue to Platform
                    </Button>
                </DialogActions>
            </StyledDialog>
            {/* === FINE POPUP BETA === */}
            
            <PageToRender />
        </ThemeProvider>
    );
}

export default App;